import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Download, FileCheck2, ShieldCheck, UserCheck } from "lucide-react";
import api from "@/lib/api";

const CHECKLIST_URL = "/recursos/checklist-delegar-sin-improvisar.pdf";
const DELEGAR_URL = "/registro/abogado?next=/app/delegaciones/nueva&source=blog_checklist";

const checklist = [
  "La identidad profesional está verificada.",
  "La tarea está descrita sin ambigüedades.",
  "La ciudad, tribunal y fecha son correctos.",
  "Los antecedentes necesarios están disponibles.",
  "El honorario, los gastos y las condiciones son conocidos.",
  "Se acordó qué respaldo debe entregarse.",
  "Se definió qué ocurre ante una cancelación.",
  "El estado y la entrega quedarán documentados.",
];

function setMeta(name, content) {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function track(event, data = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...data });
}

function LeadForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", need: "delegar", consent: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (field) => (event) => {
    const value = field === "consent" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.consent) {
      setError("Necesitamos tu autorización para enviarte la guía y comunicaciones relacionadas.");
      return;
    }
    setStatus("sending");
    setError("");
    track("checklist_form_submit", { need: form.need });
    try {
      await api.post("/early-access", {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.need,
        message: "Solicitó el checklist Delega sin improvisar",
        source: "lead_magnet_checklist_delegar",
      });
      track("checklist_submit", { need: form.need });
      navigate("/gracias/checklist-delegar-sin-improvisar", {
        state: { name: form.name.trim(), checklistUrl: CHECKLIST_URL },
      });
    } catch (requestError) {
      setStatus("error");
      setError("No pudimos registrar la solicitud. Intenta nuevamente o escríbenos a soporte@colegalink.cl.");
    }
  };

  return (
    <aside id="descargar-checklist" className="rounded-3xl border border-emerald-300/20 bg-slate-950 p-6 text-white shadow-2xl shadow-slate-950/20 md:p-8">
      <span className="text-xs font-bold uppercase tracking-[0.22em] text-teal-300">Recurso gratuito</span>
      <h2 className="mt-3 text-2xl font-black leading-tight md:text-3xl">Antes de delegar, revisa estos 20 puntos.</h2>
      <p className="mt-3 text-sm leading-6 text-slate-300">Recibe la guía para definir el encargo, las condiciones económicas, la evidencia y el cierre.</p>
      <form className="mt-6 space-y-4" onSubmit={submit} onFocus={() => track("checklist_form_start")}>
        <label className="block text-sm font-semibold">
          Nombre
          <input required value={form.name} onChange={update("name")} className="mt-2 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-white outline-none transition focus:border-teal-300" placeholder="Tu nombre" />
        </label>
        <label className="block text-sm font-semibold">
          Correo profesional
          <input required type="email" value={form.email} onChange={update("email")} className="mt-2 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-white outline-none transition focus:border-teal-300" placeholder="nombre@estudio.cl" />
        </label>
        <label className="block text-sm font-semibold">
          ¿Qué necesitas hoy?
          <select value={form.need} onChange={update("need")} className="mt-2 w-full rounded-xl border border-white/15 bg-slate-900 px-4 py-3 text-white outline-none transition focus:border-teal-300">
            <option value="delegar">Delegar una gestión</option>
            <option value="realizar_gestiones">Realizar gestiones para otros abogados</option>
            <option value="conocer_plataforma">Sólo conocer la plataforma</option>
          </select>
        </label>
        <label className="flex cursor-pointer items-start gap-3 text-xs leading-5 text-slate-300">
          <input type="checkbox" checked={form.consent} onChange={update("consent")} className="mt-1 h-4 w-4 accent-teal-400" />
          <span>Autorizo el envío de la guía y comunicaciones relacionadas. Puedo dejar de recibirlas cuando quiera.</span>
        </label>
        {error && <p className="rounded-xl bg-red-400/10 px-4 py-3 text-sm text-red-200" role="alert">{error}</p>}
        <button disabled={status === "sending"} className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-400 px-5 py-3.5 font-black text-slate-950 transition hover:bg-teal-300 disabled:cursor-wait disabled:opacity-70">
          <Download className="h-5 w-5" />
          {status === "sending" ? "Preparando tu guía…" : "Recibir el checklist"}
        </button>
      </form>
      <p className="mt-4 text-center text-xs text-slate-400">¿Necesitas ayuda? soporte@colegalink.cl</p>
    </aside>
  );
}

function Risk({ number, title, children }) {
  return (
    <section className="border-t border-slate-200 py-8">
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-black text-amber-800">{number}</span>
        <div>
          <h2 className="text-xl font-black text-slate-950 md:text-2xl">{title}</h2>
          <div className="mt-3 space-y-4 leading-7 text-slate-650">{children}</div>
        </div>
      </div>
    </section>
  );
}

export default function DelegarWhatsappArticle() {
  useEffect(() => {
    document.title = "Delegar una gestión jurídica por WhatsApp: riesgos | Colega Link";
    setMeta("description", "Conoce los riesgos de delegar audiencias y gestiones jurídicas por WhatsApp y qué condiciones definir antes de encargar el trabajo.");
    setMeta("robots", "index, follow");
    track("article_view", { article: "delegar_gestion_whatsapp" });
  }, []);

  return (
    <main className="bg-white text-slate-700">
      <header className="border-b border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 text-white">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
          <div className="max-w-4xl">
            <Link to="/blog" className="text-sm font-bold text-teal-300 hover:text-teal-200">Blog Colega Link</Link>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-teal-300">Delegación jurídica · Guía práctica</p>
            <h1 className="mt-4 text-4xl font-black leading-[1.08] md:text-6xl">Delegar una gestión jurídica por WhatsApp: riesgos y una alternativa más ordenada</h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300">Encontrar un contacto no equivale a construir una delegación profesional. Revisa qué debería quedar claro antes de confiar una audiencia, diligencia o presentación.</p>
            <div className="mt-8 flex flex-wrap gap-3 text-sm font-semibold text-slate-200">
              <span className="rounded-full border border-white/15 bg-white/5 px-4 py-2">Lectura: 7 minutos</span>
              <span className="rounded-full border border-white/15 bg-white/5 px-4 py-2">Para abogados mandantes y delegados</span>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-12 px-5 py-14 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <article className="min-w-0">
          <p className="text-xl font-semibold leading-8 text-slate-900">Cuando necesitas cubrir una audiencia o realizar una diligencia fuera de tu ciudad, la solución habitual suele comenzar con: “¿Alguien tiene un contacto?”</p>
          <p className="mt-5 leading-7">WhatsApp sirve para comunicarse. El problema aparece cuando se transforma al mismo tiempo en directorio profesional, acuerdo de honorarios, repositorio de documentos, sistema de seguimiento y comprobante de entrega.</p>

          <div className="my-10 rounded-3xl border border-teal-200 bg-teal-50 p-6 md:p-8">
            <h2 className="text-2xl font-black text-slate-950">El problema no es WhatsApp: es delegar sin un proceso.</h2>
            <p className="mt-3 leading-7">Una gestión puede involucrar plazos, antecedentes sensibles, honorarios y consecuencias para un cliente. Las condiciones esenciales deben conocerse antes de comenzar.</p>
          </div>

          <Risk number="1" title="No saber con certeza quién realizará la gestión">
            <p>Un nombre y una fotografía en una red social no bastan para comprobar la identidad profesional. La verificación inicial debe ocurrir antes de compartir antecedentes sensibles o transferir dinero.</p>
          </Risk>
          <Risk number="2" title="No definir exactamente qué debe hacerse">
            <p>“Ir al tribunal” puede significar presentar un escrito, retirar copias, revisar un expediente o asistir a una audiencia. El lugar, plazo, alcance y respaldo esperado deben describirse sin ambigüedades.</p>
          </Risk>
          <Risk number="3" title="No saber si la gestión realmente se realizará">
            <p>Una confirmación por mensaje no reemplaza el seguimiento. Mandante y delegado necesitan conocer el estado del encargo y disponer de evidencia del cumplimiento.</p>
          </Risk>
          <Risk number="4" title="Descubrir el precio o las condiciones demasiado tarde">
            <p>El valor puede depender de la ciudad, urgencia, tiempo, traslados y complejidad. Debe quedar claro qué incluye el honorario, qué gastos podrían existir y qué sucede si cambia el encargo.</p>
          </Risk>
          <Risk number="5" title="Cumplir y después tener que perseguir el pago">
            <p>La informalidad también perjudica al delegado. Antes de aceptar debería conocer el monto, la condición que habilita el pago, el respaldo requerido y el procedimiento ante una diferencia.</p>
          </Risk>
          <Risk number="6" title="No saber qué ocurrirá con el dinero si no se realiza">
            <p>El mandante enfrenta la preocupación inversa: pagar y no saber qué ocurrirá ante una cancelación o incumplimiento. Las condiciones aplicables deben revisarse antes de transferir dinero y no depender de promesas informales.</p>
          </Risk>

          <section className="mt-10 rounded-3xl bg-slate-50 p-6 md:p-8">
            <h2 className="text-2xl font-black text-slate-950">Del contacto informal a una gestión documentada</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                [UserCheck, "Identidad profesional", "Ambas partes saben con quién están tratando."],
                [FileCheck2, "Alcance definido", "La tarea, el plazo y la entrega quedan claros."],
                [ShieldCheck, "Condiciones visibles", "Honorarios, gastos y reglas se conocen antes."],
                [Check, "Cierre documentado", "El resultado y su respaldo quedan registrados."],
              ].map(([Icon, title, copy]) => (
                <div key={title} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <Icon className="h-6 w-6 text-teal-600" />
                  <h3 className="mt-3 font-black text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6">{copy}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="text-3xl font-black text-slate-950">Checklist antes de delegar</h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {checklist.map((item) => (
                <div key={item} className="flex gap-3 rounded-2xl border border-slate-200 p-4">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" />
                  <span className="text-sm font-semibold leading-6 text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-3xl bg-slate-950 p-7 text-white md:p-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-300">Una alternativa más ordenada</span>
            <h2 className="mt-3 text-3xl font-black">Delega sin improvisar.</h2>
            <p className="mt-4 max-w-2xl leading-7 text-slate-300">Colega Link reúne la identidad profesional, el encargo, las condiciones y la entrega en un flujo pensado para abogados que necesitan apoyo territorial.</p>
            <Link to={DELEGAR_URL} onClick={() => track("delegar_cta_click", { location: "article_bottom" })} className="mt-6 inline-flex rounded-xl bg-teal-400 px-6 py-3 font-black text-slate-950 hover:bg-teal-300">Publicar mi próxima gestión</Link>
          </section>

          <div className="mt-8 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>Este contenido es informativo. Revisa las condiciones efectivamente disponibles para cada encargo. Si necesitas orientación, escribe a soporte@colegalink.cl.</p>
          </div>
        </article>

        <div className="lg:sticky lg:top-24">
          <LeadForm />
        </div>
      </div>
    </main>
  );
}
